import {
  type ApiErrorCode,
  ApiErrorSchema,
  type LoginResponse,
  LoginResponseSchema,
} from '@gestao/shared'
import { toast } from '@gestao/ui'
import type { z } from 'zod'

import { appConfig } from '../app/config'
import * as t from '../i18n/pt-BR'
import { session } from './session'
import { broadcastSession } from './session-sync'

/** Códigos da API e dois do cliente: sem resposta e resposta fora do contrato. */
export type ClientErrorCode = ApiErrorCode | 'NETWORK_ERROR' | 'INVALID_RESPONSE'

export class ApiRequestError extends Error {
  readonly status: number
  readonly code: ClientErrorCode
  readonly details: unknown
  /** Segundos do cabeçalho Retry-After (429), quando vier. */
  readonly retryAfter: number | null

  constructor(init: {
    status: number
    code: ClientErrorCode
    message: string
    details?: unknown
    retryAfter?: number | null
  }) {
    super(init.message)
    this.name = 'ApiRequestError'
    this.status = init.status
    this.code = init.code
    this.details = init.details
    this.retryAfter = init.retryAfter ?? null
  }
}

export function isApiError(error: unknown, code?: ClientErrorCode): error is ApiRequestError {
  return error instanceof ApiRequestError && (code === undefined || error.code === code)
}

/** Texto para mostrar ao usuário a partir de qualquer erro (sempre em pt-BR, nunca vazio). */
export function errorMessage(error: unknown): string {
  if (!(error instanceof ApiRequestError)) return t.errors.byCode.INTERNAL_ERROR
  if (error.code === 'NETWORK_ERROR') return t.errors.network
  if (error.code === 'INVALID_RESPONSE') return t.errors.invalidResponse
  if (error.code === 'RATE_LIMITED' && error.retryAfter)
    return t.errors.rateLimited(error.retryAfter)
  return error.message || t.errors.byCode[error.code]
}

/** Erros de campo de um VALIDATION_FAILED (`details: [{ path, message }]`). */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiRequestError) || !Array.isArray(error.details)) return {}
  const result: Record<string, string> = {}
  for (const issue of error.details) {
    if (issue && typeof issue === 'object' && 'path' in issue && 'message' in issue) {
      const { path, message } = issue as { path: unknown; message: unknown }
      if (typeof path === 'string' && typeof message === 'string') result[path] ??= message
    }
  }
  return result
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface RequestOptions<T> {
  method?: Method
  /** JSON (objeto) ou `FormData` (multipart, enviado como está). */
  body?: unknown
  query?: Record<string, string | number | boolean | null | undefined>
  /** Valida a resposta com o schema do `@gestao/shared`. */
  schema?: z.ZodType<T>
  /** Envia o bearer e, em 401, renova a sessão e repete uma vez. Padrão: true. */
  auth?: boolean
  signal?: AbortSignal
  /** Cabeçalho Accept. Padrão: JSON. */
  accept?: string
  /** Cabeçalhos extras (por exemplo `X-Queue-Date` ao trabalhar a fila do dia). */
  headers?: Record<string, string>
}

function buildUrl(path: string, query?: RequestOptions<unknown>['query']): string {
  const url = `${appConfig.apiUrl}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  const search = params.toString()
  return search ? `${url}?${search}` : url
}

async function send(
  path: string,
  options: RequestOptions<unknown>,
  token: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {
    ...options.headers,
    Accept: options.accept ?? 'application/json',
  }
  const multipart = options.body instanceof FormData
  if (options.body !== undefined && !multipart) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body:
        options.body === undefined
          ? undefined
          : multipart
            ? (options.body as FormData)
            : JSON.stringify(options.body),
      credentials: 'include',
      signal: options.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw networkError()
  }
}

function networkError(): ApiRequestError {
  return new ApiRequestError({ status: 0, code: 'NETWORK_ERROR', message: t.errors.network })
}

function parseRetryAfter(value: string | null): number | null {
  if (!value) return null
  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds >= 0) return Math.max(1, Math.ceil(seconds))
  const date = Date.parse(value)
  return Number.isNaN(date) ? null : Math.max(1, Math.ceil((date - Date.now()) / 1000))
}

const FALLBACK_CODES: Partial<Record<number, ApiErrorCode>> = {
  400: 'VALIDATION_FAILED',
  401: 'AUTH_REQUIRED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  423: 'AUTH_LOCKED',
  429: 'RATE_LIMITED',
}

async function toError(response: Response): Promise<ApiRequestError> {
  const retryAfter = parseRetryAfter(response.headers.get('retry-after'))
  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    // Corpo ausente ou fora do formato: usa o código pelo status.
  }
  const parsed = ApiErrorSchema.safeParse(body)
  if (parsed.success) {
    return new ApiRequestError({
      status: response.status,
      code: parsed.data.code,
      message: parsed.data.message,
      details: parsed.data.details,
      retryAfter,
    })
  }
  const code = FALLBACK_CODES[response.status] ?? 'INTERNAL_ERROR'
  return new ApiRequestError({
    status: response.status,
    code,
    message: t.errors.byCode[code],
    retryAfter,
  })
}

async function parseBody<T>(response: Response, schema?: z.ZodType<T>): Promise<T> {
  const text = response.status === 204 ? '' : await response.text()
  if (!text) return undefined as T
  const invalid = new ApiRequestError({
    status: response.status,
    code: 'INVALID_RESPONSE',
    message: t.errors.invalidResponse,
  })
  let json: unknown
  try {
    json = JSON.parse(text)
  } catch {
    throw invalid
  }
  if (!schema) return json as T
  const parsed = schema.safeParse(json)
  if (!parsed.success) throw invalid
  return parsed.data
}

/* ------------------------------------------------------------------------------------------ */
/* Renovação da sessão                                                                         */
/* ------------------------------------------------------------------------------------------ */

const REFRESH_LOCK = 'gestao:auth-refresh'
let inflightRefresh: Promise<LoginResponse | null> | null = null

/** Exclusão mútua entre abas (Web Locks); sem suporte, roda direto (a fila local continua). */
function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  return locks ? locks.request(REFRESH_LOCK, task) : task()
}

async function performRefresh(staleToken: string | null): Promise<LoginResponse | null> {
  // Outra aba (ou outra chamada) já renovou depois do token que falhou: usa o novo.
  const current = session.get()
  if (current.accessToken && current.user && current.accessToken !== staleToken) {
    return { accessToken: current.accessToken, user: current.user }
  }
  let response: Response
  try {
    response = await fetch(buildUrl('/auth/refresh'), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
  } catch {
    throw networkError()
  }
  if (response.status === 401 || response.status === 403) return null
  if (!response.ok) throw await toError(response)
  const data = await parseBody(response, LoginResponseSchema)
  session.signIn(data)
  broadcastSession(data)
  return data
}

/**
 * Renova a sessão pelo cookie de refresh, uma vez só por vez: chamadas simultâneas (várias
 * requisições com 401, o StrictMode, outras abas pelo lock) esperam a mesma renovação.
 * Devolve null quando não há sessão válida; lança `NETWORK_ERROR` sem resposta da API.
 *
 * @param staleToken token que falhou (null na primeira carga). Se o token atual já for outro,
 * ele é usado sem nova renovação.
 */
export function refreshSession(staleToken: string | null): Promise<LoginResponse | null> {
  inflightRefresh ??= withRefreshLock(() => performRefresh(staleToken)).finally(() => {
    inflightRefresh = null
  })
  return inflightRefresh
}

/** Sessão que existia e deixou de valer: avisa uma vez e volta ao login (via guard). */
function expireSession(): void {
  if (session.get().status !== 'authenticated') return
  toast.warning(t.session.expired, { id: 'session-expired' })
  session.end('expired')
}

function isPasswordChangeRequired(details: unknown): boolean {
  return (
    typeof details === 'object' &&
    details !== null &&
    (details as { reason?: unknown }).reason === 'PASSWORD_CHANGE_REQUIRED'
  )
}

/* ------------------------------------------------------------------------------------------ */
/* Requisição                                                                                  */
/* ------------------------------------------------------------------------------------------ */

/** Envia a requisição com o token dado (null: sem bearer) e devolve a resposta crua. */
type Sender = (token: string | null) => Promise<Response>

/**
 * Executa com o bearer em memória. Em 401: se já existe um token mais novo, repete com ele; senão
 * renova a sessão (fila única) e repete uma vez. Se não der, a sessão expira (toast + login).
 * Devolve só respostas 2xx; as demais viram `ApiRequestError`.
 */
async function authorized(sender: Sender, auth: boolean): Promise<Response> {
  let token = auth ? session.get().accessToken : null
  let response = await sender(token)

  if (auth && response.status === 401) {
    const renewed = await refreshSession(token)
    if (!renewed) {
      const error = await toError(response)
      expireSession()
      throw error
    }
    token = renewed.accessToken
    response = await sender(token)
    if (response.status === 401) {
      const error = await toError(response)
      expireSession()
      throw error
    }
  }

  if (!response.ok) {
    const error = await toError(response)
    if (error.status === 403 && isPasswordChangeRequired(error.details)) {
      session.requirePasswordChange()
    }
    throw error
  }
  return response
}

/**
 * Requisição à API com o bearer em memória e o cookie (`credentials: 'include'`), com a renovação
 * da sessão descrita em `authorized`. Erros sempre como `ApiRequestError`.
 */
export async function apiRequest<T = void>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  const response = await authorized((token) => send(path, options, token), options.auth ?? true)
  return parseBody(response, options.schema)
}

/** Nome do arquivo no Content-Disposition (`filename*=UTF-8''…` ou `filename="…"`). */
export function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null
  const encoded = /filename\*\s*=\s*utf-8''([^;]+)/i.exec(header)?.[1]
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim())
    } catch {
      // Codificação inválida: tenta o filename simples.
    }
  }
  const plain = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(header)
  const name = (plain?.[1] ?? plain?.[2])?.trim()
  return name ? name : null
}

export interface DownloadedFile {
  blob: Blob
  /** Nome sugerido pela API (Content-Disposition), quando o navegador deixa ler o cabeçalho. */
  fileName: string | null
  /** Cabeçalhos da resposta (por exemplo `X-Export-Rows` das exportações). */
  headers: Headers
}

/** Download autenticado (o bearer não vai num `<a href>`): devolve o conteúdo e o nome sugerido. */
export async function apiDownload(
  path: string,
  options: Pick<RequestOptions<never>, 'query' | 'signal' | 'accept' | 'method' | 'body'> = {},
): Promise<DownloadedFile> {
  const response = await authorized(
    (token) => send(path, { ...options, accept: options.accept ?? '*/*' }, token),
    true,
  )
  return {
    blob: await response.blob(),
    fileName: fileNameFromDisposition(response.headers.get('content-disposition')),
    headers: response.headers,
  }
}

export interface UploadOptions<T> {
  schema?: z.ZodType<T>
  /** Fração enviada (0 a 1), quando o navegador informa o tamanho. */
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
}

function xhrHeaders(raw: string): Headers {
  const headers = new Headers()
  for (const line of raw.trim().split(/[\r\n]+/)) {
    const separator = line.indexOf(':')
    if (separator > 0) {
      headers.append(line.slice(0, separator).trim(), line.slice(separator + 1).trim())
    }
  }
  return headers
}

/** POST multipart por XMLHttpRequest: o fetch não informa o andamento do envio. */
function sendForm(
  path: string,
  form: FormData,
  token: string | null,
  options: UploadOptions<unknown>,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const abortError = () => new DOMException('Envio cancelado.', 'AbortError')
    const { signal, onProgress } = options
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const xhr = new XMLHttpRequest()
    const onAbort = () => xhr.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    const settle = () => signal?.removeEventListener('abort', onAbort)

    xhr.open('POST', buildUrl(path))
    xhr.withCredentials = true
    xhr.setRequestHeader('Accept', 'application/json')
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total)
    })
    xhr.addEventListener('load', () => {
      settle()
      const empty = xhr.status === 204 || xhr.responseText === ''
      resolve(
        new Response(empty ? null : xhr.responseText, {
          status: xhr.status,
          headers: xhrHeaders(xhr.getAllResponseHeaders()),
        }),
      )
    })
    xhr.addEventListener('error', () => {
      settle()
      reject(networkError())
    })
    xhr.addEventListener('abort', () => {
      settle()
      reject(abortError())
    })
    xhr.send(form)
  })
}

/**
 * Envio de arquivo (multipart) com progresso e a mesma renovação de sessão das demais
 * requisições. Em 401 o formulário é reenviado uma vez com o token novo.
 */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  options: UploadOptions<T> = {},
): Promise<T> {
  const response = await authorized((token) => sendForm(path, form, token, options), true)
  return parseBody(response, options.schema)
}

/** Atalhos por método. */
export const api = {
  get: <T>(path: string, options: Omit<RequestOptions<T>, 'method' | 'body'> = {}) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T = void>(path: string, body?: unknown, options: Omit<RequestOptions<T>, 'method'> = {}) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T = void>(
    path: string,
    body?: unknown,
    options: Omit<RequestOptions<T>, 'method'> = {},
  ) => apiRequest<T>(path, { ...options, method: 'PATCH', body }),
}
